import { Avatar, Divider, IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { Role } from 'common/src/types/enums/Role.enum';
import {
  IOrganization,
  IOrganizationGroup,
  IPage,
  IUser,
} from 'common/src/types/interfaces';
import { ICreateUserDto, IUpdateUserDto } from 'common/src/types/user.dtos';

import { QUERY_KEYS } from 'portal/src/api/constants';
import { getOrganizationGroups } from 'portal/src/api/OrganizationGroupsService';
import { getOrganizations } from 'portal/src/api/OrganizationsService';
import { addUserService, updateUserService } from 'portal/src/api/UsersService';
import useAuth from 'portal/src/auth/useAuth';
import { FormButtonLayout } from 'portal/src/components/common/forms/FormButtonColumn';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { formControlErrors } from 'portal/src/components/common/forms/formControlErrors';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import AlertPreferencesForm from 'portal/src/components/systemAdministration/Form/AlertPreferences/AlertPreferencesForm';
import { trackAlertPreferenceChanges } from 'portal/src/components/systemAdministration/Form/AlertPreferences/tracking';
import { AccountProviderIds } from 'portal/src/constants/AccountProviderIds';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { useHasAlertsFeature } from 'portal/src/hooks/useHasAlertsFeature';
import { getNameInitials } from 'portal/src/utils/names';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { defaultValues, getFields, IUserFormValues } from './UserForm.fields';

import ApiKeyDialog from './Dialogs/ApiKeyDialog';
import ProfilePictureDialog from './Dialogs/ProfilePictureDialog';
import ChangePasswordForm from './PasswordForms/ChangePasswordForm';

const UserForm = ({
  backButtonlabel,
  buttonLayout = 'side',
  entity = {},
  handleBackAction,
  header,
  isNewOrg = false,
  isPending,
  organizationId,
  onSave,
  orgFieldDisabled,
  isProfileView = false,
  removeContainerStyle = false,
  saveOrUpdateLabel,
}: {
  backButtonlabel?: string;
  buttonLayout?: FormButtonLayout;
  entity?: Partial<IUser>;
  handleBackAction?: () => void;
  header?: string;
  isNewOrg?: boolean;
  isPending?: boolean;
  organizationId?: string;
  onSave?: (data: IUserFormValues) => void;
  orgFieldDisabled?: boolean;
  /**
   * This prop is needed to distinguish between the user settings view and
   * seeing the logged-in user's details under the organization page.
   * */
  isProfileView?: boolean;
  removeContainerStyle?: boolean;
  saveOrUpdateLabel?: string;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const queryClient = useQueryClient();
  const {
    getUser,
    getUserPicture,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    refreshUser,
    verifyUserAccountProvider,
  } = useAuth();
  const loggedInUser = getUser();
  const { id } = loggedInUser;

  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [showProfilePicDialog, setShowProfilePicDialog] = useState(false);
  const [showManagePwScreen, setShowManagePwScreen] = useState<boolean>(false);

  const userInitials = getNameInitials(entity.name || '');
  const profilePicSrc = getUserPicture() || '';
  const loggedInUserIsProfile = entity.id === id;

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields, isDirty },
    ...formControl
  } = useForm({
    defaultValues: defaultValues(entity, organizationId),
  });

  const hasAlertsFeature = useHasAlertsFeature();
  const entityIsLoggedInUser = loggedInUser.id === entity.id;

  const selectedUserType = useWatch({
    control,
    name: 'userType',
  });

  const hasClientSysAdminAccess = hasClientSystemAdminAccess();

  const [{ data: organizations }, { data: organizationGroups }] = useQueries({
    queries: [
      {
        queryKey: ['organizations'],
        queryFn: async () =>
          await getOrganizations({ sort: 'name', sortDirection: 'asc' }),
        initialData: { data: [] } as IPage<IOrganization>,
        enabled: hasClientSysAdminAccess,
      },
      {
        queryKey: ['organizationGroups'],
        queryFn: async () => await getOrganizationGroups(),
        initialData: { data: [] } as IPage<IOrganizationGroup>,
        enabled: hasClientSysAdminAccess,
      },
    ],
  });

  const canEditAlertPreferences = entity.id
    ? entityIsLoggedInUser || hasClientSysAdminAccess
    : hasAdminAccess() && buttonLayout === 'side';

  const formFieldsConfiguration = getFields(
    loggedInUser,
    {
      id: entity.id ?? NEW_ID,
      organizationId: entity.organizationId ?? organizationId,
      orgGroups: entity.organizationGroups ?? [],
    },
    organizations.data,
    organizationGroups.data,
    selectedUserType as Role,
    isNewOrg,
    orgFieldDisabled ?? true,
  );

  const {
    data: userAccountProvider,
    isError: isAcctProviderError,
    isFetching: isFetchingAcctProvider,
  } = useQuery({
    queryKey: ['userAccountProvider'],
    queryFn: async () => await verifyUserAccountProvider(),
    initialData: {},
  });

  const { mutate: addOrUpdateUser, isPending: mutationIsPending } = useMutation(
    {
      mutationFn: async (payload: IUserFormValues) => {
        const formattedPayload = prepareFormPayload(payload, dirtyFields);

        if (formattedPayload.organizationGroups) {
          (formattedPayload as any).organizationGroups =
            payload.organizationGroups.map((id) => ({ id }));
        }

        if (entity.id) {
          await updateUserService(
            entity.id,
            formattedPayload as unknown as IUpdateUserDto,
          );
          reset(payload, { keepValues: true });
          snackbarHandler.success(MESSAGES.USER.UPDATED);
        } else {
          const createPayload = formattedPayload as unknown as ICreateUserDto;
          if (!createPayload.organizationId) {
            createPayload.organizationId = organizationId;
          }
          await addUserService(createPayload);
          snackbarHandler.success(MESSAGES.USER.CREATED);
        }

        trackAlertPreferenceChanges({
          original: entity.alertPreferences ?? [],
          updated: payload.alertPreferences,
          targetUserId: entity.id,
          editedSelf: entityIsLoggedInUser,
        });

        if (loggedInUserIsProfile) {
          await refreshUser();
          // if a user is updating their alert preferences, force the alert queries to refresh
          if (formattedPayload.alertPreferences) {
            await queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.alerts],
            });
            await queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.alertsBadgeCount],
            });
          }
          return;
        }

        handleBackAction?.();
      },
      onError: (err) => {
        if (ApiError.compatible(err)) {
          formControlErrors(formControl, err);
        }

        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    },
  );

  if (showManagePwScreen && loggedInUserIsProfile) {
    return (
      <ChangePasswordForm
        onCancel={() => setShowManagePwScreen(false)}
        onSaveChange={() => setShowManagePwScreen(false)}
        providerId={userAccountProvider.providerId}
      />
    );
  }

  const profileExtraButtons = loggedInUserIsProfile
    ? [
        {
          disabled: isFetchingAcctProvider || isAcctProviderError,
          onClick: () => setShowManagePwScreen(true),
          label:
            userAccountProvider.providerId === AccountProviderIds.DEFAULT
              ? 'Change Password'
              : 'Reset Password',
        },
        {
          onClick: () => setShowApiKeyDialog(true),
          label: 'Generate API Key',
        },
      ]
    : undefined;

  const resetForm = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.users, entity.id],
    });
    reset(defaultValues(entity, organizationId));
  };

  const submitHandler = handleSubmit((data) => {
    if (onSave) {
      onSave(data as IUserFormValues);
      return;
    }

    addOrUpdateUser(data);
  });

  return (
    <FormContainer
      backButtonlabel={backButtonlabel}
      buttonLayout={buttonLayout}
      beforeMainFields={
        loggedInUserIsProfile && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Typography variant="body2">Profile Picture:</Typography>
            <IconButton onClick={() => setShowProfilePicDialog(true)}>
              <Avatar src={profilePicSrc} sx={{ width: 56, height: 56 }}>
                {userInitials}
              </Avatar>
            </IconButton>
          </Stack>
        )
      }
      control={control}
      extraButtons={profileExtraButtons}
      allowReadOnlyEdit={loggedInUserIsProfile}
      handleBackAction={
        isProfileView && loggedInUserIsProfile ? undefined : handleBackAction
      }
      handleSubmit={submitHandler}
      header={header ?? (loggedInUserIsProfile ? '' : 'User Settings')}
      isDirty={isDirty}
      isNew={!entity.id}
      isPending={isPending ?? mutationIsPending}
      removeContainerStyle={removeContainerStyle}
      resetForm={isProfileView && loggedInUserIsProfile ? resetForm : undefined}
      saveOrUpdateLabel={saveOrUpdateLabel}
      mainFieldsConfig={formFieldsConfiguration}
      afterMainFields={
        <>
          {hasAlertsFeature && canEditAlertPreferences && (
            <>
              <Divider sx={{ mt: 4, mb: 2 }}>Alert Preferences</Divider>
              <AlertPreferencesForm
                control={control}
                entityIsLoggedInUser={entityIsLoggedInUser}
              />
            </>
          )}

          <ApiKeyDialog
            open={showApiKeyDialog}
            onClose={() => setShowApiKeyDialog(false)}
          />
          <ProfilePictureDialog
            open={showProfilePicDialog}
            onClose={() => setShowProfilePicDialog(false)}
            user={{
              id: entity.id,
              initials: userInitials,
              values: { profilePicSrc, file: null },
            }}
          />
        </>
      }
    />
  );
};

export default UserForm;
